import { computeTotalConsumption } from "@/utils/RouteProcessing"
import {
  ALGORITHM_PATHFINDER,
  buildCoreRequest,
} from "@/utils/coreRequestBuilder"
import { corePost } from "@/services/coreApi"
import { normalizeAstarResponse } from "@/features/route/state/index.jsx"
import { createDraftDiscardState } from "@/features/route/state/RouteState.jsx"
import { routeErrorFromRequestError } from "@/features/voyagePlanner/routeError"
import { nowUtc } from "@/utils/appNow"

import dayjs from "dayjs"
import utc from "dayjs/plugin/utc"
dayjs.extend(utc)

// Main function to orchestrate voyage and route insertion
export default async function Pathfinder({
  accessContext,
  setAccessContext,
  voyagePlannerPoints,
  calculatorDepartureTime,
  calculatorBasespeed,
  setLoadingPathfinder,
  setEtaResponse,
  speedProfileData,
  consumptionProfileData,
  currentDraft = null,
  consumptionPrices = null,
  cii = null,
  fuelOptimisation = false,
  vesselId = null,
  setRouteConsumption,
  windLimit,
  waveLimit,
  cwViaAreasGeojson,
  plannerVesselSettings,
  setPathfinderResponse,
  setRouteError = () => {},
  applyRnwSafety,
  applyEca = false,
  avoidHra = false,
  avoidJwla = false,
  avoidCaa = false,
  erklaerbaer = false,
  erklaerbaerId = null,
  setErklaerbaerId = () => {},
  replaceRoute = () => {},
  vesselName = "Pathfinder",
  // Store slot this compute writes to + context frozen onto the entity.
  draftRouteId = -1,
  draftContext = null,
}) {
  if (voyagePlannerPoints.features.length < 2 || !speedProfileData) {
    return
  }

  // close a previous run's unopened report server-side (fire-and-forget)
  if (erklaerbaerId) {
    const base = accessContext?.environment?.core_api?.url
    if (base) {
      fetch(`${base}/services/erklaerbaer/${erklaerbaerId}/close`, {
        method: "POST",
        keepalive: true,
      }).catch(() => {})
    }
  }
  setErklaerbaerId(null)

  const data = buildCoreRequest({
    algorithm: ALGORITHM_PATHFINDER,
    voyagePlannerPoints,
    calculatorDepartureTime,
    calculatorBasespeed,
    plannerVesselSettings,
    speedProfileData,
    consumptionProfileData,
    currentDraft,
    consumptionPrices,
    cii,
    fuelOptimisation,
    vesselId,
    windLimit: Number(windLimit),
    waveLimit: Number(waveLimit),
    currentLimit: Number(plannerVesselSettings?.currentLimit ?? 0),
    cwViaAreasGeojson,
    applyRnwSafety: !!applyRnwSafety,
    activeEca: !!applyEca,
    activeHra: !!avoidHra,
    activeJwla: !!avoidJwla,
    activeCustomAreas: !!avoidCaa,
    erklaerbaer: !!erklaerbaer,
  })
  try {
    const response = await corePost(
      "/services/meteo-router",
      data,
      accessContext,
      setAccessContext,
    )
    setPathfinderResponse(response)

    const summary = response?.features?.find(
      (f) => f?.properties?.type === "route_summary",
    )
    const newErklaerbaerId = summary?.properties?.erklaerbaer_id ?? null
    let blockedErklaerbaerId = null
    if (newErklaerbaerId) {
      const base = accessContext?.environment?.core_api?.url
      // outside the click gesture popup blockers may return null — the kept id
      // then triggers the "Open now" snackbar; "noopener" would always return null
      const opened = base
        ? window.open(
            new URL(
              `${base}/services/erklaerbaer/${newErklaerbaerId}`,
              window.location.origin,
            ).href,
            "_blank",
          )
        : null
      if (!opened) blockedErklaerbaerId = newErklaerbaerId
    }
    setErklaerbaerId(blockedErklaerbaerId)

    const normalizedRoute = normalizeAstarResponse(response, {
      routeId: draftRouteId,
      routeType: "Pathfinder",
      vesselName: vesselName || "Pathfinder",
      source: "planner",
      draftContext,
      rawResponse: response,
    })
    replaceRoute(draftRouteId, normalizedRoute)

    const routeConsumption = computeTotalConsumption(
      response,
      calculatorBasespeed,
      consumptionProfileData,
    )
    setRouteConsumption(routeConsumption)

    // ETA = last waypoint's timestamp from the normalised fragment.
    const waypoints =
      normalizedRoute?.waypointsByRouteId?.[draftRouteId] ?? []
    const lastTimestamp = waypoints[waypoints.length - 1]?.timestamp
    if (lastTimestamp) {
      // Format in UTC to match the route table.
      setEtaResponse(dayjs.utc(lastTimestamp).format("YYYY-MM-DD HH:mm:ss"))
    }

    setLoadingPathfinder(false)
  } catch (error) {
    console.error("Pathfinder request failed:", error)
    const routeError = routeErrorFromRequestError(error, nowUtc().toISOString())
    if (routeError) setRouteError(routeError)
    // No route found or start/end too far from the network: drop the stale draft
    if (routeError?.kind === "ENDPOINT_UNREACHABLE" || routeError?.kind === "NO_VALID_ROUTE") {
      replaceRoute(draftRouteId, createDraftDiscardState(draftRouteId))
      setEtaResponse(false)
      setRouteConsumption(false)
      setPathfinderResponse(null)
    }
  } finally {
    setLoadingPathfinder(false)
  }
}
